"use client";

import { useState } from "react";
import { FaApple, FaGoogle } from "react-icons/fa";
import { AuthPanel } from "~/components/auth/AuthPanel";
import { SocialAuthButton } from "~/components/auth/SocialAuthButton";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Paragraph } from "~/components/ui/Typography";
import { authClient } from "~/lib/auth/client";
import { isValidEmail, isValidUSPhone, looksLikePhone, toE164 } from "~/lib/auth/validation";
type IdentifyStepProps = {
  initialInput: string;
  /**
   * Optional transient note shown above the form — e.g. a failed OAuth/magic-link
   * exchange handed over from the callback, or a failed sign-in completion.
   */
  notice?: string | null;
  next: string;
  onPhoneOtpSent: (phone: string, rawInput: string) => void;
  onEmailOtpSent: (email: string) => void;
  onOAuthStarted: () => void;
};
export function IdentifyStep({
  initialInput,
  notice,
  next,
  onPhoneOtpSent,
  onEmailOtpSent,
  onOAuthStarted
}: IdentifyStepProps) {
  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const handleContinue = async () => {
    setError("");
    const trimmed = input.trim();
    if (!trimmed) {
      setError("Please enter your phone number or email.");
      return;
    }
    const isPhone = looksLikePhone(trimmed);
    if (isPhone && !isValidUSPhone(trimmed)) {
      setError("Please enter a valid 10-digit US phone number.");
      return;
    }
    if (!isPhone && !isValidEmail(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setIsSubmitting(true);
    try {
      if (isPhone) {
        const e164 = toE164(trimmed);
        await authClient.sendOtp("phone", e164);
        onPhoneOtpSent(e164, trimmed);
      } else {
        await authClient.sendOtp("email", trimmed);
        onEmailOtpSent(trimmed);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    if (error) setError("");
  };
  const handleInputKeyDown = (e_0: React.KeyboardEvent<HTMLInputElement>) => {
    if (e_0.key === "Enter") void handleContinue();
  };
  const handleGoogleAuth = async () => {
    setError("");
    setIsGoogleLoading(true);
    try {
      await authClient.signInWithGoogle(`${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`);
      onOAuthStarted();
    } catch (err_0) {
      setError(err_0 instanceof Error ? err_0.message : "Something went wrong. Please try again.");
      setIsGoogleLoading(false);
    } finally {
      setIsGoogleLoading(false);
    }
  };
  const handleAppleAuth = async () => {
    setError("");
    setIsAppleLoading(true);
    try {
      await authClient.signInWithApple(`${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`);
      onOAuthStarted();
    } catch (err_1) {
      setError(err_1 instanceof Error ? err_1.message : "Something went wrong. Please try again.");
      setIsAppleLoading(false);
    } finally {
      setIsAppleLoading(false);
    }
  };
  return <AuthPanel title="What's your phone number or email?" subtitle="Sign in or create an account">
      {notice ? <div className="mb-4 rounded-lg border border-danger/30 bg-danger/5 px-4 py-3">
          <Paragraph size="sm" className="text-danger">
            {notice}
          </Paragraph>
        </div> : null}

      <div className="mb-3 sm:mb-4">
        <Input type="text" inputMode="email" placeholder="Enter phone number or email" autoComplete="username" value={input} onChange={handleInputChange} onKeyDown={handleInputKeyDown} className={error ? "border-danger focus:border-danger" : undefined} />
        {error ? <Paragraph size="sm" className="mt-1.5 text-danger">
            {error}
          </Paragraph> : null}
      </div>

      <Button className="mb-4 w-full" disabled={isSubmitting} onClick={handleContinue} type="button" variant="primary" size="lg">
        {isSubmitting ? "Sending..." : "Continue"}
      </Button>

      <div className="mb-4 flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <Paragraph size="sm" className="text-muted">
          or
        </Paragraph>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="mb-4 flex flex-col gap-2">
        <SocialAuthButton icon={<FaGoogle className="h-[18px] w-[18px]" />} disabled={isGoogleLoading} onClick={handleGoogleAuth}>
          {isGoogleLoading ? "Redirecting..." : "Continue with Google"}
        </SocialAuthButton>

        <SocialAuthButton icon={<FaApple className="h-[18px] w-[18px]" />} disabled={isAppleLoading} onClick={handleAppleAuth}>
          {isAppleLoading ? "Redirecting..." : "Continue with Apple"}
        </SocialAuthButton>
      </div>

      <Paragraph size="sm" className="text-center text-muted">
        By continuing, you agree to receive calls or texts from EZSpot. Message
        and data rates may apply.
      </Paragraph>
    </AuthPanel>;
}